class AddToCartBulk extends HTMLElement {

  connectedCallback() {
    this.productId = this.dataset.productId;
    this.cartQuantity = Number(this.dataset.cartQuantity || 0);

    this.variants = JSON.parse(this.dataset.variants || '[]');

    this.tiers = JSON.parse(this.dataset.discountTiers || '[]')
      .map(t => ({ min: Number(t.min), percent: Number(t.percent) }))
      .sort((a, b) => a.min - b.min);

    this.rows = Array.from(this.querySelectorAll('.bulk-variant'));

    this.bar        = this.querySelector('.bulk-tier-bar');
    this.tierPoints = Array.from(this.querySelectorAll('.bulk-tier-point'));
    this.resetBtn   = this.querySelector('.bulk-reset');

    this.summaryQty   = this.querySelector('.bulk-summary-qty');
    this.summaryUnit  = this.querySelector('.bulk-summary-unit');
    this.summarySave  = this.querySelector('.bulk-summary-save');
    this.summaryFinal = this.querySelector('.bulk-summary-final');

    this._applyInventoryConstraints();
    this._initQtyControls();
    this._bindReset();
    this._setupCartSync();
    this._updateCartInfo();
    this._recalculate();
  }

  /* ---------------- Utils ---------------- */

  _hasMixedPrices() {
    const prices = this.variants.map(v => v.price);
    return prices.some(p => p !== prices[0]);
  }

  _applyDiscount(price, percent) {
    if (!percent) return price;
    const disc = Math.floor(price * percent / 100);
    return price - disc;
  }

  _formatMoney(cents) {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency', currency: 'EUR'
    }).format(cents / 100);
  }

  /* ---------------- Inventory constraints ---------------- */

  _applyInventoryConstraints() {
    this.rows.forEach((row, i) => {
      const variant             = this.variants[i];
      const inventoryManagement = row.dataset.inventoryManagement
        ?? variant?.inventory_management
        ?? null;
      const stock = Number(row.dataset.inventory
        ?? variant?.inventory_quantity
        ?? 0);

      const input    = row.querySelector('input');
      const btnPlus  = row.querySelector('.qty-plus');
      const btnMinus = row.querySelector('.qty-minus');
      const stockEl  = row.querySelector('.bulk-stock');

      const isTracked = !!inventoryManagement;
      if (!isTracked) return;

      if (stockEl) {
        if (stock > 0) {
          stockEl.textContent = `Verfügbar: ${stock} Stk`;
          if (stock <= 5) stockEl.classList.add('bulk-stock--low');
        }
      }

      if (stock <= 0) {
        input.value       = 0;
        input.disabled    = true;
        btnPlus.disabled  = true;
        btnMinus.disabled = true;
        row.classList.add('bulk-variant--unavailable');
        return;
      }

      input.max = stock;
      row.dataset.maxStock = stock;
    });
  }

  _clampToStock(row, input) {
    const max = Number(row.dataset.maxStock);
    if (!max) return;
    if (Number(input.value) > max) {
      input.value = max;
    }
  }

  /* ---------------- Cart sync ---------------- */

  _setupCartSync() {
    if (typeof window.subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this._cartUpdateUnsub = subscribe(
        window.PUB_SUB_EVENTS.cartUpdate,
        () => this._syncCartQuantity()
      );
    }

    this._cartObserver = new MutationObserver(() => this._syncCartQuantity());

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      this._cartObserver.observe(drawer, { childList: true, subtree: true });
    }
  }

  async _syncCartQuantity() {
    try {
      const cart = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      }).then(r => r.json());

      const newCartQty = cart.items
        .filter(item => item.product_id == this.productId)
        .reduce((sum, item) => sum + item.quantity, 0);

      if (newCartQty !== this.cartQuantity) {
        this.cartQuantity = newCartQty;
        this._updateCartInfo();
        this._recalculate();
      }
    } catch (e) {
      console.warn('[add-to-cart-bulk] cart sync failed', e);
    }
  }

  _updateCartInfo() {
    let cartInfoContainer = this.querySelector('.bulk-cart-info');
    let cartInfo = this.querySelector('.bulk-cart-current');

    if (this.cartQuantity > 0) {
      if (!cartInfoContainer) {
        cartInfoContainer = document.createElement('div');
        cartInfoContainer.className = 'bulk-cart-info';
        const title = this.querySelector('h2');
        if (title && title.nextSibling) {
          title.parentNode.insertBefore(cartInfoContainer, title.nextSibling);
        } else {
          this.prepend(cartInfoContainer);
        }
      }
      if (!cartInfo) {
        cartInfo = document.createElement('span');
        cartInfo.className = 'bulk-cart-current';
        cartInfoContainer.appendChild(cartInfo);
      }
      cartInfo.innerHTML = `Im Warenkorb: <strong>${this.cartQuantity} Stk</strong>`;
      cartInfoContainer.style.display = 'block';
    } else {
      if (cartInfoContainer) cartInfoContainer.style.display = 'none';
    }
  }

  disconnectedCallback() {
    if (this._cartUpdateUnsub) this._cartUpdateUnsub();
    if (this._cartObserver) this._cartObserver.disconnect();
  }

  /* ---------------- Controls ---------------- */

  reset() {
    this.rows.forEach(row => {
      if (!row.classList.contains('bulk-variant--unavailable')) {
        row.querySelector('input').value = 0;
      }
    });
    this._recalculate();
  }

  _initQtyControls() {
    this.rows.forEach(row => {
      if (row.classList.contains('bulk-variant--unavailable')) return;

      const input = row.querySelector('input');
      const minus = row.querySelector('.qty-minus');
      const plus  = row.querySelector('.qty-plus');

      minus.addEventListener('click', () => {
        input.value = Math.max(0, Number(input.value) - 1);
        this._recalculate();
      });

      plus.addEventListener('click', () => {
        const max = Number(row.dataset.maxStock) || Infinity;
        input.value = Math.min(Number(input.value) + 1, max);
        this._recalculate();
      });

      input.addEventListener('input', () => {
        input.value = Math.max(0, Number(input.value) || 0);
        this._clampToStock(row, input);
        this._recalculate();
      });
    });
  }

  _bindReset() {
    if (!this.resetBtn) return;
    this.resetBtn.addEventListener('click', () => this.reset());
  }

  /* ---------------- Payload ---------------- */

  getBulkPayload() {
    const items    = [];
    const bulkQty  = this._getBulkQuantity();
    const totalQty = this.cartQuantity + bulkQty;
    const { currentTier } = this._getTierInfo(totalQty);

    this.rows.forEach((row, i) => {
      const qty = Number(row.querySelector('input').value || 0);
      if (qty > 0) {
        const basePrice  = this.variants[i].price;
        const finalPrice = currentTier
          ? this._applyDiscount(basePrice, currentTier.percent)
          : basePrice;

        items.push({
          variant_id:    this.variants[i].variant_id,
          quantity:      qty,
          color:         this.variants[i].color,
          size:          this.variants[i].size,
          price:         finalPrice,
          product_title: this.variants[i].product_title
        });
      }
    });

    return items;
  }

  /* ---------------- Main calc ---------------- */

  _recalculate() {
    const bulkQty  = this._getBulkQuantity();
    const totalQty = this.cartQuantity + bulkQty;
    const tierInfo = this._getTierInfo(totalQty);

    this._updateProgress(bulkQty);
    this._updatePrices(tierInfo.currentTier);
    this._updateSummary(bulkQty, tierInfo.currentTier);

    this.dispatchEvent(new CustomEvent('bulk-payload-changed', {
      detail: { items: this.getBulkPayload() },
      bubbles: true
    }));
  }

  _getBulkQuantity() {
    return this.rows.reduce((sum, row) => {
      return sum + Number(row.querySelector('input')?.value || 0);
    }, 0);
  }

  _getTierInfo(totalQty) {
    let currentTier = null;
    for (let i = this.tiers.length - 1; i >= 0; i--) {
      if (totalQty >= this.tiers[i].min) {
        currentTier = this.tiers[i];
        break;
      }
    }
    return { currentTier };
  }

  /* ---------------- Progress ---------------- */

  _updateProgress(bulkQty) {
    const totalQty = this.cartQuantity + bulkQty;

    if (this.bar && this.tiers.length > 0) {
      const maxMin   = this.tiers[this.tiers.length - 1].min;
      const progress = Math.min((totalQty / maxMin) * 100, 100);
      this.bar.style.width = `${progress}%`;
    }

    let currentMarked = false;
    this.tierPoints.forEach(point => {
      const min = Number(point.dataset.min);
      point.classList.remove('is-reached', 'is-current');
      if (totalQty >= min) {
        point.classList.add('is-reached');
        return;
      }
      if (!currentMarked) {
        point.classList.add('is-current');
        currentMarked = true;
      }
    });
  }

  /* ---------------- Prices ---------------- */

  _updatePrices(currentTier) {
    const mixed = this._hasMixedPrices();

    this.rows.forEach((row, i) => {
      const priceEl   = row.querySelector('.bulk-price');
      const basePrice = this.variants[i].price;
      const final     = currentTier
        ? this._applyDiscount(basePrice, currentTier.percent)
        : basePrice;

      priceEl.textContent = mixed ? ` (${this._formatMoney(final)})` : '';
    });
  }

  /* ---------------- Summary ---------------- */

  _updateSummary(bulkQty, currentTier) {
    if (this.summaryUnit) {
      if (this._hasMixedPrices()) {
        const prices = this.variants.map(v =>
          currentTier ? this._applyDiscount(v.price, currentTier.percent) : v.price
        );
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        this.summaryUnit.textContent = min === max
          ? this._formatMoney(min)
          : `${this._formatMoney(min)} – ${this._formatMoney(max)}`;
      } else {
        let unitPrice = this.variants[0]?.price ?? 0;
        if (currentTier) unitPrice = this._applyDiscount(unitPrice, currentTier.percent);
        this.summaryUnit.textContent = this._formatMoney(unitPrice);
      }
    }

    let baseTotal  = 0;
    let finalTotal = 0;

    this.rows.forEach((row, i) => {
      const qty   = Number(row.querySelector('input').value || 0);
      const price = this.variants[i].price;
      baseTotal  += qty * price;
      finalTotal += qty * (currentTier
        ? this._applyDiscount(price, currentTier.percent)
        : price);
    });

    const saved = baseTotal - finalTotal;

    if (this.summaryQty) {
      const cartText = this.cartQuantity > 0
        ? ` (+${this.cartQuantity} im Warenkorb)` : '';
      this.summaryQty.textContent = `${bulkQty} Stk${cartText}`;
    }
    if (this.summarySave)  this.summarySave.textContent  = this._formatMoney(saved);
    if (this.summaryFinal) this.summaryFinal.textContent = this._formatMoney(finalTotal);
  }
}

customElements.define('add-to-cart-bulk', AddToCartBulk);